// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * A minimal PDF object reader, working directly on bytes.
 *
 * pdf.js exists and is excellent, and this tool uses it — but only as a second
 * opinion at the very end. It cannot be used for the job in this file, because
 * pdf.js's whole design goal is to make a damaged file open anyway: when its
 * cross-reference table does not parse it silently re-indexes every object in
 * the file and carries on. That behaviour is right for a viewer and fatal here,
 * where "this cross-reference table parses" is the evidence. A reader that
 * repairs what it is meant to be testing cannot test it.
 *
 * So: this is deliberately strict. It throws on anything it does not
 * understand rather than guessing, and nothing in it ever scans for objects.
 *
 * Scope is the document skeleton — dictionaries, arrays, names, numbers,
 * strings, references, streams. Content streams, fonts and images are not
 * parsed and never need to be.
 *
 * ISO 32000-1:2008 §7.2 (lexical conventions) and §7.3 (objects).
 */

/** ISO 32000-1 §7.2.2, table 1. */
export function isWhite(b: number): boolean {
  return b === 0x00 || b === 0x09 || b === 0x0a || b === 0x0c || b === 0x0d || b === 0x20;
}

/** ISO 32000-1 §7.2.2, table 2. */
export function isDelim(b: number): boolean {
  return (
    b === 0x28 || b === 0x29 || b === 0x3c || b === 0x3e || b === 0x5b ||
    b === 0x5d || b === 0x7b || b === 0x7d || b === 0x2f || b === 0x25
  );
}

export function isRegular(b: number): boolean {
  return !isWhite(b) && !isDelim(b);
}

function isDigit(b: number): boolean {
  return b >= 0x30 && b <= 0x39;
}

export class PdfName {
  constructor(readonly name: string) {}
  toString() {
    return `/${this.name}`;
  }
}

export class PdfRef {
  constructor(readonly num: number, readonly gen: number) {}
  get key(): string {
    return `${this.num}R${this.gen}`;
  }
  toString() {
    return `${this.num} ${this.gen} R`;
  }
}

/**
 * A PDF string, kept as raw bytes. Decoding is a separate decision — the same
 * bytes may be PDFDocEncoded, UTF-16BE with a byte-order mark, or (rarely, and
 * against the spec) UTF-8, and the caller knows which question it is asking.
 */
export class PdfString {
  constructor(readonly bytes: Uint8Array) {}
}

export interface PdfStream {
  dict: PdfDict;
  /** Offset of the first byte after the `stream` keyword's end-of-line. */
  start: number;
  /** Raw, still-encoded bytes. Length is taken from /Length when it is a
   *  direct integer, and otherwise found by searching for `endstream`. */
  raw: Uint8Array;
  /** True when /Length was an indirect reference or wrong and the length had
   *  to be recovered by searching. Worth surfacing: it is unusual. */
  lengthRecovered: boolean;
}

export type PdfDict = Map<string, PdfValue>;

export type PdfValue =
  | number
  | boolean
  | null
  | PdfName
  | PdfRef
  | PdfString
  | PdfValue[]
  | PdfDict
  | PdfStream;

export function isDict(v: PdfValue | undefined): v is PdfDict {
  return v instanceof Map;
}

export function isStream(v: PdfValue | undefined): v is PdfStream {
  return typeof v === 'object' && v !== null && !(v instanceof Map) && 'dict' in (v as object);
}

export class PdfSyntaxError extends Error {
  constructor(message: string, readonly offset: number) {
    super(`${message} (at byte ${offset})`);
    this.name = 'PdfSyntaxError';
  }
}

/** Advances past whitespace and `%` comments. */
export function skipWhite(buf: Uint8Array, pos: number): number {
  let i = pos;
  for (;;) {
    while (i < buf.length && isWhite(buf[i])) i += 1;
    if (i < buf.length && buf[i] === 0x25) {
      while (i < buf.length && buf[i] !== 0x0a && buf[i] !== 0x0d) i += 1;
      continue;
    }
    return i;
  }
}

/** Reads a run of regular characters — a keyword such as `obj` or `R`. */
export function readToken(buf: Uint8Array, pos: number): { token: string; end: number } {
  const start = skipWhite(buf, pos);
  let i = start;
  while (i < buf.length && isRegular(buf[i])) i += 1;
  let token = '';
  for (let j = start; j < i; j += 1) token += String.fromCharCode(buf[j]);
  return { token, end: i };
}

function parseName(buf: Uint8Array, pos: number): { value: PdfName; end: number } {
  let i = pos + 1; // skip '/'
  let out = '';
  while (i < buf.length && isRegular(buf[i])) {
    if (buf[i] === 0x23 && i + 2 < buf.length) {
      const hex = String.fromCharCode(buf[i + 1], buf[i + 2]);
      const code = Number.parseInt(hex, 16);
      if (Number.isFinite(code) && /^[0-9a-fA-F]{2}$/.test(hex)) {
        out += String.fromCharCode(code);
        i += 3;
        continue;
      }
    }
    out += String.fromCharCode(buf[i]);
    i += 1;
  }
  return { value: new PdfName(out), end: i };
}

function parseLiteralString(buf: Uint8Array, pos: number): { value: PdfString; end: number } {
  let i = pos + 1; // skip '('
  let depth = 1;
  const out: number[] = [];
  while (i < buf.length) {
    const b = buf[i];
    if (b === 0x5c) {
      // Backslash escapes, ISO 32000-1 §7.3.4.2 table 3.
      const n = buf[i + 1];
      i += 2;
      switch (n) {
        case 0x6e: out.push(0x0a); break;
        case 0x72: out.push(0x0d); break;
        case 0x74: out.push(0x09); break;
        case 0x62: out.push(0x08); break;
        case 0x66: out.push(0x0c); break;
        case 0x28: out.push(0x28); break;
        case 0x29: out.push(0x29); break;
        case 0x5c: out.push(0x5c); break;
        case 0x0d:
          // A backslash before an end-of-line marker means "no line break
          // here"; CRLF counts as one marker.
          if (buf[i] === 0x0a) i += 1;
          break;
        case 0x0a:
          break;
        default:
          if (n >= 0x30 && n <= 0x37) {
            let code = n - 0x30;
            for (let k = 0; k < 2 && buf[i] >= 0x30 && buf[i] <= 0x37; k += 1) {
              code = code * 8 + (buf[i] - 0x30);
              i += 1;
            }
            out.push(code & 0xff);
          } else {
            // "\X" for any other X is just X.
            out.push(n);
          }
      }
      continue;
    }
    if (b === 0x28) {
      depth += 1;
      out.push(b);
      i += 1;
      continue;
    }
    if (b === 0x29) {
      depth -= 1;
      i += 1;
      if (depth === 0) return { value: new PdfString(Uint8Array.from(out)), end: i };
      out.push(b);
      continue;
    }
    out.push(b);
    i += 1;
  }
  throw new PdfSyntaxError('unterminated string', pos);
}

function parseHexString(buf: Uint8Array, pos: number): { value: PdfString; end: number } {
  let i = pos + 1; // skip '<'
  const digits: number[] = [];
  while (i < buf.length && buf[i] !== 0x3e) {
    const b = buf[i];
    if (b >= 0x30 && b <= 0x39) digits.push(b - 0x30);
    else if (b >= 0x41 && b <= 0x46) digits.push(b - 0x41 + 10);
    else if (b >= 0x61 && b <= 0x66) digits.push(b - 0x61 + 10);
    else if (!isWhite(b)) throw new PdfSyntaxError('bad character in hex string', i);
    i += 1;
  }
  if (i >= buf.length) throw new PdfSyntaxError('unterminated hex string', pos);
  // An odd number of digits means the last byte is padded with a trailing 0.
  if (digits.length % 2 === 1) digits.push(0);
  const out = new Uint8Array(digits.length / 2);
  for (let k = 0; k < out.length; k += 1) out[k] = digits[k * 2] * 16 + digits[k * 2 + 1];
  return { value: new PdfString(out), end: i + 1 };
}

/**
 * Parses one object.
 *
 * `resolveLength` lets a caller supply the value of an indirect /Length so a
 * stream's extent is known exactly. Without it — and whenever /Length is
 * simply wrong, which happens — the extent is recovered by searching forward
 * for `endstream`, and the stream is marked as such.
 */
export function parseObject(
  buf: Uint8Array,
  pos: number,
  resolveLength?: (ref: PdfRef) => number | null,
): { value: PdfValue; end: number } {
  const i = skipWhite(buf, pos);
  if (i >= buf.length) throw new PdfSyntaxError('unexpected end of file', i);
  const b = buf[i];

  if (b === 0x2f) return parseName(buf, i);
  if (b === 0x28) return parseLiteralString(buf, i);

  if (b === 0x5b) {
    const arr: PdfValue[] = [];
    let j = i + 1;
    for (;;) {
      j = skipWhite(buf, j);
      if (j >= buf.length) throw new PdfSyntaxError('unterminated array', i);
      if (buf[j] === 0x5d) return { value: arr, end: j + 1 };
      const item = parseObject(buf, j, resolveLength);
      arr.push(item.value);
      j = item.end;
    }
  }

  if (b === 0x3c) {
    if (buf[i + 1] !== 0x3c) return parseHexString(buf, i);
    const dict: PdfDict = new Map();
    let j = i + 2;
    for (;;) {
      j = skipWhite(buf, j);
      if (j >= buf.length) throw new PdfSyntaxError('unterminated dictionary', i);
      if (buf[j] === 0x3e && buf[j + 1] === 0x3e) {
        j += 2;
        break;
      }
      if (buf[j] !== 0x2f) throw new PdfSyntaxError('dictionary key is not a name', j);
      const key = parseName(buf, j);
      const val = parseObject(buf, key.end, resolveLength);
      dict.set(key.value.name, val.value);
      j = val.end;
    }

    // A dictionary followed by the `stream` keyword is a stream object.
    const after = readToken(buf, j);
    if (after.token !== 'stream') return { value: dict, end: j };

    // §7.3.8.1: the keyword is followed by CRLF or a single LF, never by a
    // bare CR. Writers do emit a bare CR; tolerate it, since misreading the
    // first byte would corrupt every stream in the file.
    let start = after.end;
    if (buf[start] === 0x0d) start += 1;
    if (buf[start] === 0x0a) start += 1;

    const declared = dict.get('Length');
    let length: number | null = null;
    if (typeof declared === 'number') length = declared;
    else if (declared instanceof PdfRef && resolveLength) length = resolveLength(declared);

    let lengthRecovered = false;
    if (length === null || length < 0 || start + length > buf.length) {
      length = findEndstream(buf, start) - start;
      lengthRecovered = true;
    } else {
      // Trust /Length only if `endstream` really is where it says. A wrong
      // /Length that happens to land inside the data would otherwise truncate
      // the stream silently.
      const probe = skipWhite(buf, start + length);
      const kw = readToken(buf, probe);
      if (kw.token !== 'endstream') {
        length = findEndstream(buf, start) - start;
        lengthRecovered = true;
      }
    }
    if (length < 0) throw new PdfSyntaxError('stream has no endstream', start);

    const raw = buf.subarray(start, start + length);
    const endKw = readToken(buf, skipWhite(buf, start + length));
    const end = endKw.token === 'endstream' ? endKw.end : start + length;
    return { value: { dict, start, raw, lengthRecovered }, end };
  }

  if (b === 0x2b || b === 0x2d || b === 0x2e || isDigit(b)) {
    const num = readToken(buf, i);
    const value = Number.parseFloat(num.token.replace(/^\+/, ''));
    if (!Number.isFinite(value)) throw new PdfSyntaxError(`bad number "${num.token}"`, i);

    // `n g R` is a reference. Both parts must be non-negative integers, so a
    // real number can never begin one.
    if (Number.isInteger(value) && value >= 0 && !num.token.includes('.')) {
      const second = readToken(buf, num.end);
      if (/^\d+$/.test(second.token)) {
        const third = readToken(buf, second.end);
        if (third.token === 'R') {
          return { value: new PdfRef(value, Number.parseInt(second.token, 10)), end: third.end };
        }
      }
    }
    return { value, end: num.end };
  }

  const word = readToken(buf, i);
  if (word.token === 'true') return { value: true, end: word.end };
  if (word.token === 'false') return { value: false, end: word.end };
  if (word.token === 'null') return { value: null, end: word.end };

  throw new PdfSyntaxError(`unexpected token "${word.token || String.fromCharCode(b)}"`, i);
}

/** Byte offset of the `endstream` keyword at or after `from`, or -1. */
function findEndstream(buf: Uint8Array, from: number): number {
  const needle = [0x65, 0x6e, 0x64, 0x73, 0x74, 0x72, 0x65, 0x61, 0x6d]; // endstream
  const at = indexOfBytes(buf, needle, from);
  if (at === -1) return -1;
  // Step back over the end-of-line that precedes the keyword: it belongs to
  // the delimiter, not to the stream data.
  let end = at;
  if (end > from && buf[end - 1] === 0x0a) end -= 1;
  if (end > from && buf[end - 1] === 0x0d) end -= 1;
  return end;
}

/** Plain forward search. Used only on skeleton scans, never per-byte in a loop. */
export function indexOfBytes(hay: Uint8Array, needle: number[], from = 0): number {
  const last = hay.length - needle.length;
  outer: for (let i = Math.max(0, from); i <= last; i += 1) {
    for (let j = 0; j < needle.length; j += 1) {
      if (hay[i + j] !== needle[j]) continue outer;
    }
    return i;
  }
  return -1;
}

/** Backward search. */
export function lastIndexOfBytes(hay: Uint8Array, needle: number[], from?: number): number {
  const start = Math.min(from ?? hay.length - needle.length, hay.length - needle.length);
  outer: for (let i = start; i >= 0; i -= 1) {
    for (let j = 0; j < needle.length; j += 1) {
      if (hay[i + j] !== needle[j]) continue outer;
    }
    return i;
  }
  return -1;
}

export function asciiBytes(text: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < text.length; i += 1) out.push(text.charCodeAt(i) & 0xff);
  return out;
}

/**
 * Parses `<num> <gen> obj … endobj` at a byte offset, checking that the object
 * number found there is the one the cross-reference table promised. That check
 * is the whole point: an xref whose offsets point at the wrong objects is
 * exactly the state a phantom revision produces.
 */
export function parseIndirectObjectAt(
  buf: Uint8Array,
  offset: number,
  expectNum: number,
  resolveLength?: (ref: PdfRef) => number | null,
): PdfValue {
  const numTok = readToken(buf, offset);
  if (!/^\d+$/.test(numTok.token)) {
    throw new PdfSyntaxError(`expected an object number for object ${expectNum}`, offset);
  }
  const found = Number.parseInt(numTok.token, 10);
  if (found !== expectNum) {
    throw new PdfSyntaxError(`cross-reference points at object ${found}, not ${expectNum}`, offset);
  }
  const genTok = readToken(buf, numTok.end);
  if (!/^\d+$/.test(genTok.token)) {
    throw new PdfSyntaxError(`expected a generation number for object ${expectNum}`, numTok.end);
  }
  const objTok = readToken(buf, genTok.end);
  if (objTok.token !== 'obj') {
    throw new PdfSyntaxError(`expected "obj" for object ${expectNum}`, genTok.end);
  }
  return parseObject(buf, objTok.end, resolveLength).value;
}
